import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import { Toast } from 'react-vant'
import { APIWeb } from '@/api'
import { useMerchantConfig } from '@/hooks'
import { useTransactionRefresh } from '@/hooks'
import Big from 'big.js'
import { toFixedNoRound, formatThousands } from '@/utils'
import { APP_NAME } from '@/config/constants'
interface ExchangeData {
  fromCurrency: string
  fromAmount: string
  toCurrency: string
  toAmount: string
  fromIcon: string
  toIcon: string
}

export default React.memo(function ExchangeConfirm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const { merchantConfig, actions } = useMerchantConfig()
  const { refreshAfterTransaction } = useTransactionRefresh()

  useEffect(() => {
    actions.fetchMerchantConfig()
  }, [])

  // 获取兑换数据
  const exchangeData = useMemo(() => {
    const state = location.state as ExchangeData
    return state
  }, [location.state])

  // 如果没有数据，返回兑换页面
  if (!exchangeData) {
    navigate('/exchange', { replace: true })
    return null
  }

  // 汇率（示例：1:1）
  const exchangeRate = useMemo(() => {
    return `1 ${exchangeData.fromCurrency} ≈1 ${exchangeData.toCurrency}`
  }, [exchangeData])

  // 计算服务费和实际到账金额（使用 big.js 避免精度问题）
  const { serviceFee, actualToAmount } = useMemo(() => {
    try {
      const fromAmount = Big(exchangeData.fromAmount || 0)
      const feeRate = Big(merchantConfig?.exchange_service_fee_rate || 0)

      // 计算服务费: fromAmount * feeRate
      // 注意：后端返回的 feeRate 已经是小数形式（例如 0.1 = 10%）
      const fee = fromAmount.times(feeRate)

      // 实际到账金额 = 原金额 - 服务费（假设1:1汇率）
      const actualAmount = fromAmount.minus(fee)

      return {
        serviceFee: toFixedNoRound(fee.toString(), 4, false),
        actualToAmount: toFixedNoRound(actualAmount.toString(), 2)
      }
    } catch (error) {
      console.error('计算服务费失败:', error)
      return {
        serviceFee: '0.00',
        actualToAmount: toFixedNoRound(exchangeData.fromAmount, 2) || '0.00'
      }
    }
  }, [exchangeData.fromAmount, merchantConfig?.exchange_service_fee_rate])

  // 取消兑换
  const handleCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  // 确认兑换
  const handleConfirm = useCallback(async () => {
    if (loading) return

    try {
      setLoading(true)
      Toast.loading({
        message: t('处理中'),
        forbidClick: true
      })
      // 调用兑换 API
      await APIWeb.exchange({
        in_amount: parseFloat(exchangeData.fromAmount),
        in_type: exchangeData.fromCurrency.toLowerCase() as any,
        out_type: exchangeData.toCurrency.toLowerCase() as any
      })

      Toast.success({
        message: t('兑换成功'),
        onClose: () => {
          navigate('/home', { replace: true })
        }
      })

      // 交易成功后刷新状态
      await refreshAfterTransaction()
    } catch (error: any) {
      console.error('兑换失败:', error)
    } finally {
      setLoading(false)
    }
  }, [exchangeData, loading, navigate, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('兑换')}</span>}
      />

      <div className="bg-white px-6 py-6 flex flex-col pb-24">
        {/* 兑换图标 */}
        <div className="flex justify-center mb-6">
          <img src="/icon/exchange-icon.svg" alt="exchange" className="w-20 h-20" />
        </div>

        {/* 标题 */}
        <div className="text-center text-5 font-medium text-black mb-8">{t('兑换')}</div>

        {/* 支付信息 */}
        <div className="mb-6">
          <div className="text-3.75 text-[#0F0F0F]/60 mb-3">{t('支付')}</div>
          <div className="flex items-center">
            <img src={exchangeData.fromIcon} alt={exchangeData.fromCurrency} className="w-6 h-6 mr-3" />
            <span className="text-8 font-bold text-black">-{formatThousands(exchangeData.fromAmount, 2, true)}</span>
            <span className="text-8 text-[#979797] ml-2">{exchangeData.fromCurrency}</span>
          </div>
        </div>

        {/* 收到信息 */}
        <div className="mb-8">
          <div className="text-3.75 text-[#0F0F0F]/60 mb-3">{t('收到')}</div>
          <div className="flex items-center">
            <img src={exchangeData.toIcon} alt={exchangeData.toCurrency} className="w-6 h-6 mr-3" />
            <span className="text-8 font-bold text-black">+{actualToAmount}</span>
            <span className="text-8 text-[#979797] ml-2">{exchangeData.toCurrency}</span>
          </div>
        </div>

        {/* 详细信息 */}
        <div className="space-y-4 mb-8 border-[0.5px] border-t-solid border-[#E9E9E9] pt-5">
          {/* 服务费 */}
          <div className="flex flex-col">
            <span className="text-3.5 text-[#0F0F0F]/60">{t('服务费')}</span>
            <span className="text-3.5 text-black mt-2">{serviceFee} {exchangeData.fromCurrency} ({toFixedNoRound((merchantConfig?.exchange_service_fee_rate || 0) * 100, 2)}%)</span>
          </div>

          {/* 汇率 */}
          <div className="flex flex-col">
            <span className="text-3.5 text-[#0F0F0F]/60">{t('汇率')}</span>
            <span className="text-3.5 text-black mt-2">{exchangeRate}</span>
          </div>

          {/* 路径提示 */}
          <div className="flex flex-col">
            <span className="text-3.5 text-[#0F0F0F]/60">{t('路径此交易由第三方供应提供')}</span>
          </div>
          <div className="text-3.5 text-black mt-2">{t('{{name}} Aggregator', { name: APP_NAME })}</div>
        </div>

        {/* 按钮组 */}
        <div className="fixed bottom-6 left-6 right-6 flex gap-4">
          <CustomButton
            color='default'
            onClick={handleCancel}
            disabled={loading}
          >
            {t('取消')}
          </CustomButton>
          <CustomButton
            onClick={handleConfirm}
            className="flex-1"
            disabled={loading}
            loading={loading}
          >
            {loading ? '' : t('确认')}
          </CustomButton>
        </div>
      </div>
    </PageLayout>
  )
})
